import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination, SearchInput } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import ClientFormModal from "@/hq/clients/ClientFormModal";
import ScanWebsiteModal from "@/hq/clients/ScanWebsiteModal";
import ImportClientsModal from "@/hq/clients/ImportClientsModal";
import { DeleteClientModal, BulkDeleteModal } from "@/hq/clients/ClientDeleteModals";
import BulkFullPitchModal from "@/hq/clients/BulkFullPitchModal";
import BulkTerritoryModal from "@/hq/clients/BulkTerritoryModal";
import BulkIndustryModal from "@/hq/clients/BulkIndustryModal";
import { INDUSTRY_CATALOGUE, industryLabel } from "@/hq/industries";
import { ExternalLink, Building2, Plus, Trash2, X, Globe, Upload, Zap, Loader2 } from "lucide-react";
import OrangeScoreBadge, { classifyScore } from "@/hq/OrangeScoreBadge";
import ServiceMultiSelect from "@/hq/clients/ServiceMultiSelect";
import { TERRITORY_FILTER_OPTIONS, TERRITORY_META, territoryFlag, territoryShort } from "@/hq/territory";
import NudgeChip, { NUDGE_FILTER_OPTIONS } from "@/hq/clients/NudgeChip";
import PhoneDisplay from "@/components/site/PhoneDisplay";

function ScoreDot({ score }) {
  if (score === null || score === undefined) {
    return <span className="text-[13px] font-black text-[#B8B4AC]" data-testid="hq-client-score-empty">—</span>;
  }
  return (
    <span
      className="text-[16px] font-black tabular-nums text-[#0A0A0A]"
      data-testid={`hq-client-score-value-${Number(score)}`}
    >
      {Number(score)}
    </span>
  );
}

function TerritoryPill({ value }) {
  if (!value) {
    return (
      <span data-testid="hq-client-territory-notset"
            className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.08em] uppercase text-[#8A8A8A] border border-[#EAEAEA] bg-[#F6F5F2]">
        NOT SET
      </span>
    );
  }
  const meta = TERRITORY_META[value] || {};
  return (
    <span
      data-testid={`hq-client-territory-${value}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black tracking-[0.08em] uppercase text-[#0A0A0A] border border-[#EAEAEA] bg-white whitespace-nowrap"
      title={`Territory: ${value}`}
      aria-label={`Territory ${value}`}
    >
      <span aria-hidden>{meta.flag || territoryFlag(value)}</span>
      {meta.short || territoryShort(value)}
    </span>
  );
}

// Phase 3I.4P — Latest successful sales pitch pill for the Next Action column.
// Derived server-side from canonical `core_communications` successful sends —
// never from a frontend-only flag. Rendered instead of the recommendation
// nudge once ANY successful pitch has been recorded for this client.
const SENT_PITCH_LABEL = {
  website:  "Website Sent",
  optimise: "Optimise Sent",
  crm:      "CRM Sent",
  full:     "Full Pitch Sent",
};
function SentPitchPill({ pitch, testId }) {
  if (!pitch || !pitch.pitch_type) return null;
  const label = SENT_PITCH_LABEL[pitch.pitch_type] || `${pitch.pitch_type} Sent`;
  const title = pitch.sent_at
    ? `Last ${pitch.pitch_type} pitch sent ${new Date(pitch.sent_at).toLocaleString()}`
    : `Last ${pitch.pitch_type} pitch sent`;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-black tracking-[0.06em] uppercase whitespace-nowrap"
      style={{ color: "#0F7B3E", background: "#EBFBF2", border: "1px solid #B7EBCF" }}
      title={title}
    >
      <span aria-hidden style={{ color: "#0FA958", fontWeight: 900 }}>✓</span>
      {label}
    </span>
  );
}

function StatusPill({ value }) {
  const tone = {
    active:    { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
    prospect:  { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
    inactive:  { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
    cancelled: { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
    archived:  { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
    none:      { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
  }[value] || { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.14em] uppercase"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}>{value || "—"}</span>
  );
}

const FILTERS = [
  { key: "",           label: "All (excl. archived)" },
  { key: "active",     label: "Active" },
  { key: "prospect",   label: "Prospects" },
  { key: "test_demo",  label: "Test / demo records" },
  { key: "archived",   label: "Archived" },
];

const SCORE_FILTERS = [
  { key: "",             label: "All scores" },
  { key: "RED_ORANGE",   label: "🍊 Red orange (0-79)" },
  { key: "ORANGE",       label: "🍊 Orange (80-89)" },
  { key: "GREEN_ORANGE", label: "🍊 Green orange (90-100)" },
  { key: "NOT_SCANNED",  label: "Not scanned" },
];
const SCORE_SORTS = [
  { key: "",         label: "Sort: default" },
  { key: "score_asc",  label: "Score: low → high" },
  { key: "score_desc", label: "Score: high → low" },
];

export default function HQClients() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [scoreSort, setScoreSort] = useState("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [nudgeFilter, setNudgeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rowScanFor, setRowScanFor] = useState(null);   // {id, website}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFullPitchOpen, setBulkFullPitchOpen] = useState(false);
  const [bulkTerritoryOpen, setBulkTerritoryOpen] = useState(false);
  const [bulkIndustryOpen, setBulkIndustryOpen] = useState(false);
  const [industryFilter, setIndustryFilter] = useState("");
  const [rescanningIds, setRescanningIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reload = React.useCallback(async () => {
    // Phase 3I.4P.3.3 — Auto-refresh contract:
    //  1. Awaitable Promise so callers (single scan → add, bulk promote,
    //     lightning-bolt rescan, spreadsheet import) can `await reload()`
    //     BEFORE showing final success state.
    //  2. Verifies the response has the canonical shape (has `items`).
    //  3. On refetch failure it emits a clear warning toast instead of
    //     silently leaving stale rows on screen.
    try {
      const r = await hqApi.get("/control/clients", { params: {
        q: debouncedQ || undefined, page, page_size: 25,
        filter_key: filterKey || undefined,
        territory: territoryFilter || undefined,
        industry: industryFilter || undefined,
        nudge_filter: nudgeFilter || undefined,
      } });
      if (!r?.data || !Array.isArray(r.data.items)) {
        toast.warning("Clients updated successfully, but the list could not refresh automatically.");
        return null;
      }
      setData(r.data);
      return r.data;
    } catch (_) {
      toast.warning("Clients updated successfully, but the list could not refresh automatically.");
      return null;
    }
  }, [debouncedQ, page, filterKey, territoryFilter, industryFilter, nudgeFilter]);

  useEffect(() => {
    let alive = true;
    hqApi.get("/control/clients", { params: {
      q: debouncedQ || undefined, page, page_size: 25,
      filter_key: filterKey || undefined,
      territory: territoryFilter || undefined,
      industry: industryFilter || undefined,
      nudge_filter: nudgeFilter || undefined,
    } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading clients."));
    return () => { alive = false; };
  }, [debouncedQ, page, filterKey, territoryFilter, industryFilter, nudgeFilter]);

  useEffect(() => { setSelected(new Set()); }, [debouncedQ, page, filterKey, territoryFilter, industryFilter, nudgeFilter]);

  if (err) return <Card className="p-6" data-testid="hq-clients-error">{err}</Card>;

  const toggle = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const allVisibleIds = (data?.items || []).map((c) => c.id);
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const toggleAllVisible = () => {
    const s = new Set(selected);
    if (allVisibleSelected) allVisibleIds.forEach((id) => s.delete(id));
    else allVisibleIds.forEach((id) => s.add(id));
    setSelected(s);
  };

  return (
    <div className="space-y-5" data-testid="hq-clients">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Clients</div>
          <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">Every business, one view</h1>
          <p className="text-[11px] tracking-[0.06em] text-[#8A8A8A] mt-1" data-testid="hq-clients-count">
            {data ? `${data.total} clients` : "Loading…"} · Search, filter and manage every client
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="hq-clients-scan"
            onClick={() => setScanOpen(true)}
            className="px-4 py-2.5 rounded-full bg-white border border-[#EAEAEA] hover:border-[#FF6A00] text-[11px] font-black tracking-[0.16em] uppercase"
          >
            <Globe size={13} className="inline mr-1"/> Scan website
          </button>
          <button
            data-testid="hq-clients-import"
            onClick={() => setImportOpen(true)}
            className="px-4 py-2.5 rounded-full bg-white border border-[#EAEAEA] hover:border-[#FF6A00] text-[11px] font-black tracking-[0.16em] uppercase"
          >
            <Upload size={13} className="inline mr-1"/> Import clients
          </button>
          <button
            data-testid="hq-clients-add"
            onClick={() => setAddOpen(true)}
            className="px-4 py-2.5 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
          >
            <Plus size={13} className="inline mr-1"/> Add client
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search clients…" testId="hq-clients-search"/>
        <select
          data-testid="hq-clients-filter"
          value={filterKey}
          onChange={(e) => { setFilterKey(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border"
        >
          {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select
          data-testid="hq-clients-score-filter"
          value={scoreFilter}
          onChange={(e) => setScoreFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border"
        >
          {SCORE_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select
          data-testid="hq-clients-score-sort"
          value={scoreSort}
          onChange={(e) => setScoreSort(e.target.value)}
          className="px-3 py-2 rounded-lg border"
        >
          {SCORE_SORTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select
          data-testid="hq-clients-territory-filter"
          value={territoryFilter}
          onChange={(e) => { setTerritoryFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border"
        >
          {TERRITORY_FILTER_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select
          data-testid="hq-clients-nudge-filter"
          value={nudgeFilter}
          onChange={(e) => { setNudgeFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border"
        >
          {NUDGE_FILTER_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select
          data-testid="hq-clients-industry-filter"
          value={industryFilter}
          onChange={(e) => { setIndustryFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-lg border"
        >
          <option value="">All industries</option>
          <option value="NOT_SET">Not set</option>
          {INDUSTRY_CATALOGUE.map((row) => (
            <option key={row.slug} value={row.slug}>{row.label}</option>
          ))}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#0A0A0A] text-white" data-testid="hq-clients-bulk-toolbar">
          <span className="text-[11px] tracking-[0.16em] uppercase font-black">
            {selected.size} client{selected.size === 1 ? "" : "s"} selected
            <span className="ml-2 text-white/50 tracking-normal normal-case font-normal">
              (max 25 per bulk pitch)
            </span>
          </span>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setBulkFullPitchOpen(true)}
              disabled={selected.size > 25}
              data-testid="hq-clients-bulk-full-pitch"
              className={`px-4 py-2 rounded-full text-black text-[10px] font-black tracking-[0.16em] uppercase ${selected.size > 25 ? "bg-white/20 cursor-not-allowed text-white/50" : "bg-[#FF6A00] hover:bg-[#e75f00]"}`}
              title={selected.size > 25 ? "Reduce to 25 or fewer clients to bulk send" : "Send FULL OrangeBySite pitch to selected clients"}
            >
              🍊 Bulk full pitch
            </button>
            <button
              onClick={() => setBulkTerritoryOpen(true)}
              disabled={selected.size > 25}
              data-testid="hq-clients-bulk-territory"
              className="px-3 py-2 rounded-full text-white text-[10px] font-black tracking-[0.16em] uppercase bg-white/10 hover:bg-white/20 disabled:opacity-40"
            >
              Set territory
            </button>
            <button
              onClick={() => setBulkIndustryOpen(true)}
              disabled={selected.size > 25}
              data-testid="hq-clients-bulk-industry"
              className="px-3 py-2 rounded-full text-white text-[10px] font-black tracking-[0.16em] uppercase bg-white/10 hover:bg-white/20 disabled:opacity-40"
            >
              Set industry
            </button>
            <button onClick={() => setBulkOpen(true)} data-testid="hq-clients-bulk-delete" className={`px-4 py-2 rounded-full text-white text-[10px] font-black tracking-[0.16em] uppercase ${filterKey === "archived" ? "bg-[#C1350B]" : "bg-white/10 hover:bg-white/20"}`}>
              <Trash2 size={12} className="inline mr-1"/> {filterKey === "archived" ? "Delete permanently" : "Archive selected"}
            </button>
            <button onClick={() => setSelected(new Set())} data-testid="hq-clients-clear-selection" className="px-4 py-2 rounded-full border border-white/30 text-[10px] font-black tracking-[0.16em] uppercase">
              <X size={12} className="inline mr-1"/> Clear selection
            </button>
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <div>
          <table className="w-full text-[13px] table-fixed" data-testid="hq-clients-table">
            {/* Phase 3I.4P.3 — column widths sum to 100% so the table
                never overflows its container. Column order:
                SELECT → BUSINESS → INDUSTRY → CONTACT → EMAIL → PHONE →
                WEBSITE → TERR. → SERVICES → NEXT ACTION → SCORE → ACTIONS.
                Status is intentionally NOT a visible column (data field
                itself is unchanged). */}
            <colgroup>
              <col style={{ width: "36px" }}/>{/* checkbox */}
              <col style={{ width: "12%" }}/>{/* business */}
              <col style={{ width: "10%" }}/>{/* industry */}
              <col style={{ width: "9%" }}/> {/* contact */}
              <col style={{ width: "15%" }}/>{/* email */}
              <col style={{ width: "11%" }}/>{/* phone */}
              <col style={{ width: "11%" }}/>{/* website */}
              <col style={{ width: "5%" }}/> {/* territory */}
              <col style={{ width: "9%" }}/> {/* services */}
              <col style={{ width: "9%" }}/> {/* next action */}
              <col style={{ width: "5%" }}/> {/* orange score */}
              <col style={{ width: "60px" }}/>{/* actions */}
            </colgroup>
            <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">
              <tr>
                <th className="px-2 py-3">
                  <input type="checkbox" data-testid="hq-clients-select-all" checked={allVisibleSelected} onChange={toggleAllVisible}/>
                </th>
                <th className="text-left px-3 py-3">Business</th>
                <th className="text-left px-3 py-3">Industry</th>
                <th className="text-left px-3 py-3">Contact</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3 whitespace-nowrap">Phone</th>
                <th className="text-left px-3 py-3">Website</th>
                <th className="text-left px-3 py-3 whitespace-nowrap">Terr.</th>
                <th className="text-left px-3 py-3">Services</th>
                <th className="text-left px-3 py-3 whitespace-nowrap">Next action</th>
                <th className="text-left px-3 py-3 whitespace-nowrap">Score</th>
                <th className="text-left px-2 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i} className="border-t border-[#EAEAEA]">
                    <td className="px-3 py-3" colSpan={12}><Skeleton h={20}/></td>
                  </tr>
                ))
              ) : data.items?.length === 0 ? (
                <tr><td colSpan={12}><EmptyState title="No clients yet" hint="Add your first client or clear the filter." testId="hq-clients-empty"/></td></tr>
              ) : (
                data.items.map((c) => c).filter((c) => {
                  if (!scoreFilter) return true;
                  const cls = classifyScore(c.orange_score);
                  return cls === scoreFilter;
                }).sort((a, b) => {
                  if (scoreSort === "score_asc")  return (a.orange_score ?? 999) - (b.orange_score ?? 999);
                  if (scoreSort === "score_desc") return (b.orange_score ?? -1) - (a.orange_score ?? -1);
                  return 0;
                }).map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-[#EAEAEA] hover:bg-[#FFF7EE]"
                    data-testid={`hq-client-row-${c.id}`}
                  >
                    <td className="px-2 py-3">
                      <input type="checkbox" data-testid={`hq-client-check-${c.id}`} checked={selected.has(c.id)} onChange={() => toggle(c.id)} onClick={(e) => e.stopPropagation()}/>
                    </td>
                    <td className="px-3 py-3 cursor-pointer" onClick={() => navigate(`/hq/clients/${c.id}`)} title={c.business_name || ""}>
                      <div className="font-black text-[13px] flex items-center gap-1.5 truncate">
                        <Building2 size={13} className="text-[#FF6A00] shrink-0"/>
                        <span className="truncate">{c.business_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap overflow-hidden text-ellipsis text-[#4A4A4A]"
                        title={industryLabel(c.industry) || ""}
                        data-testid={`hq-client-industry-${c.id}`}>
                      {industryLabel(c.industry) || <span className="text-[#B8B4AC]">—</span>}
                    </td>
                    <td className="px-3 py-3 truncate" title={c.contact_name || ""}>{c.contact_name || "—"}</td>
                    <td className="px-3 py-3 text-[#4A4A4A] truncate" title={c.email || ""}>{c.email || "—"}</td>
                    <td className="px-3 py-3 text-[#4A4A4A] whitespace-nowrap" data-testid={`hq-client-phone-${c.id}`}>
                      <PhoneDisplay value={c.phone || ""} compact testId={`hq-client-phone-${c.id}-display`}/>
                    </td>
                    <td className="px-3 py-3">
                      {c.website ? (
                        <a onClick={(e) => e.stopPropagation()} href={c.website} target="_blank" rel="noreferrer"
                          title={c.website}
                          className="inline-flex items-center gap-1 text-[#FF6A00] hover:underline truncate max-w-full">
                          <span className="truncate">{c.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
                          <ExternalLink size={11} className="shrink-0"/>
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap"><TerritoryPill value={c.territory}/></td>
                    <td className="px-3 py-3 text-[#4A4A4A]" onClick={(e) => e.stopPropagation()}>
                      <ServiceMultiSelect
                        businessId={c.id}
                        slots={c.service_slots || []}
                        onChange={(items) => {
                          setData((prev) => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              items: prev.items.map((row) => row.id === c.id
                                ? { ...row, service_slots: items,
                                    services: items.map((it) => it.label),
                                    selected_service_keys: items.map((it) => it.service_key) }
                                : row),
                            };
                          });
                        }}
                      />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {c.last_successful_pitch ? (
                        <SentPitchPill
                          pitch={c.last_successful_pitch}
                          testId={`hq-client-pitch-sent-${c.id}`}
                        />
                      ) : (
                        <NudgeChip
                          nudge={c.next_best_action}
                          onClick={() => {
                            const a = c.next_best_action?.action;
                            if (!a) return;
                            const tab = { invoices: "invoices", leads: "leads", subs: "subs",
                                          services: "services", preview: "overview",
                                          rescan: "overview" }[a] || "overview";
                            navigate(`/hq/clients/${c.id}?tab=${tab}`);
                          }}
                          testId={`hq-client-nudge-${c.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap"><ScoreDot score={c.orange_score}/></td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-0.5">
                        <button
                          data-testid={`hq-client-rescan-${c.id}`}
                          disabled={rescanningIds.has(c.id)}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (rescanningIds.has(c.id)) return;
                            if (!c.website) {
                              toast.error("Add a website before running a scan.");
                              return;
                            }
                            setRescanningIds((prev) => {
                              const n = new Set(prev); n.add(c.id); return n;
                            });
                            try {
                              const r = await hqApi.post(`/control/clients/${c.id}/rescan`, {});
                              if (r.data?.success === false) {
                                toast.warning(r.data.message || "Scan failed. Previous score preserved.");
                              } else {
                                toast.success(`Scan complete · Orange Score ${r.data?.orange_score ?? "—"}`);
                              }
                              // Phase 3I.4P.3.3 — await the refetch so the
                              // updated score / next-action is visible before
                              // the button spinner clears.
                              await reload();
                            } catch (err) {
                              const detail = err.response?.data?.detail || "Rescan failed.";
                              toast.error(String(detail));
                            } finally {
                              setRescanningIds((prev) => {
                                const n = new Set(prev); n.delete(c.id); return n;
                              });
                            }
                          }}
                          className="p-1 rounded-full text-[#FF6A00] hover:bg-[#FFF7EE] disabled:opacity-50 disabled:cursor-not-allowed"
                          title={rescanningIds.has(c.id) ? "Scanning…" : "Scan / rescan website"}
                          aria-label={rescanningIds.has(c.id) ? "Scanning" : "Scan or rescan website"}
                        >
                          {rescanningIds.has(c.id)
                            ? <Loader2 size={13} className="animate-spin"/>
                            : <Zap size={13}/>}
                        </button>
                        <button
                          data-testid={`hq-client-delete-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: c.id, name: c.business_name }); }}
                          className="p-1 rounded-full text-[#C1350B] hover:bg-[#FFECEC]"
                          title="Delete / archive client"
                        >
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && <div className="p-4"><Pagination page={page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-clients"/></div>}
      </Card>

      <ClientFormModal open={addOpen} initial={null} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); reload(); }}/>
      <ScanWebsiteModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onDone={() => { setScanOpen(false); reload(); }}
        onRefetch={reload}
      />
      <ImportClientsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { reload(); }}
      />
      <BulkFullPitchModal
        open={bulkFullPitchOpen}
        businessIds={Array.from(selected)}
        clientsById={Object.fromEntries((data?.items || []).map((c) => [c.id, c]))}
        onClose={() => setBulkFullPitchOpen(false)}
        onSent={() => { setBulkFullPitchOpen(false); setSelected(new Set()); reload(); }}
      />
      <BulkTerritoryModal
        open={bulkTerritoryOpen}
        businessIds={Array.from(selected)}
        onClose={() => setBulkTerritoryOpen(false)}
        onDone={() => { setBulkTerritoryOpen(false); setSelected(new Set()); reload(); }}
      />
      <BulkIndustryModal
        open={bulkIndustryOpen}
        businessIds={Array.from(selected)}
        onClose={() => setBulkIndustryOpen(false)}
        onDone={() => { setBulkIndustryOpen(false); setSelected(new Set()); reload(); }}
      />
      <DeleteClientModal
        open={!!deleteTarget}
        businessId={deleteTarget?.id}
        businessName={deleteTarget?.name}
        onClose={() => setDeleteTarget(null)}
        onDone={() => { setDeleteTarget(null); reload(); }}
      />
      <BulkDeleteModal
        open={bulkOpen}
        businessIds={Array.from(selected)}
        filterKey={filterKey}
        onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); setSelected(new Set()); reload(); }}
      />
    </div>
  );
}
